<template>
  <div class="compair-layout--box">
    <div class="compair-layout--box-header">
      <h3>Filter</h3>
      <div class="vx-row">
        <div class="vx-col w-full lg:w-1/3">
          <DataInput
            v-model="filter.fromTime"
            type="date"
            label="Von"
            name="from"
            :config="{
              altFormat: 'd.m.Y H:i',
              dateFormat: 'Y-m-d H:i'
            }"
          />
        </div>
        <div class="vx-col w-full lg:w-1/3">
          <DataInput
            v-model="filter.toTime"
            type="date"
            label="Bis"
            name="to"
            :config="{
              altFormat: 'd.m.Y H:i',
              dateFormat: 'Y-m-d H:i'
            }"
          />
        </div>
        <div class="vx-col w-full lg:w-1/3">
          <DataInput
            v-model="filter.interval"
            type="select"
            :options="[
              { code: 1440, label: 'Täglich' },
              { code: 10080, label: '7 Tage' }
            ]"
            placeholder="Interval"
            name="interval"
            label="Interval"
          />
        </div>
        <div class="vx-col w-full lg:w-1/3 mt-3">
          <DataInput
            v-model="filter.grouping"
            type="select"
            :options="[
              { code: 'CITY', label: 'Stadt' },
              { code: 'COUNTRY', label: 'Land' },
              { code: 'REQUEST_OS', label: 'Betriebssystem' },
              { code: 'REQUEST_DEVICE', label: 'Gerät' },
              { code: 'REQUEST_BROWSER', label: 'Browser' },
              { code: 'SOURCE', label: 'Quelle' }
            ]"
            placeholder="Gruppierung nach"
            name="interval"
            label="Gruppierung nach"
            required
          />
        </div>
        <div class="vx-col w-full lg:w-1/3 mt-3">
          <DataInput
            v-model="filter.vacancyInfo.occupationCategory"
            type="model"
            label="Berufe - Kategorie"
            placeholder="Berufe - Kategorie"
            name="occupationField"
            multiple
            gql-query="occupationCategories"
            allow-null
          />
        </div>
        <div class="vx-col w-full lg:w-1/3 mt-3">
          <DataInput
            v-model="filter.vacancyInfo.userGroup"
            type="model"
            label="Benutzergruppe"
            placeholder="Benutzergruppe"
            multiple
            name="group"
            gql-query="userGroups"
            allow-null
          />
        </div>
        <div class="vx-col w-full lg:w-1/3 mt-3">
          <DataInput
            v-model="filter.vacancyID"
            type="model"
            placeholder="Stelle"
            name="vacancy"
            label="Stellen"
            model-label="title"
            gql-query="vacancyPage"
            gql-filter-type="objectFilter"
            object-filter-attribute="searchQuery"
            multiple
            allow-null
          />
        </div>
        <div class="vx-col w-full lg:w-1/3 mt-3">
          <DataInput
            v-model="dataSource"
            type="select"
            label="Daten"
            name="dataSource"
            :options="[
              { code: 'amount', label: 'Besuche' },
              { code: 'amountUnique', label: 'Eindeutige Besuche' },
              { code: 'dropRate', label: 'Absprungrate (in %)' },
              { code: 'duration', label: 'Sitzungsdauer' }
            ]"
          />
        </div>
        <div class="vx-col w-full">
          <DataInput
            v-model="filter.url"
            label="Webseiten URL (ohne Parameter z.B. https://www.select-gmbh.de/jobs)"
            name="url"
          />
        </div>
      </div>
    </div>
    <div class="compair-layout--box-body">
      <template v-if="datacollection">
        <VacancyStatChart
          v-if="filter"
          ref="chart"
          :chart-data="datacollection"
          :height="250"
          :max="chartMaxY"
          :options="options"
        ></VacancyStatChart>
        <VacancyPolarAreaChart
          ref="piechart"
          :chart-data="pieChartData"
          :height="150"
        ></VacancyPolarAreaChart>
      </template>
      <template v-else>
        Für die angegeben Filter gibt es aktuell keine Daten.
      </template>
    </div>
  </div>
</template>

<script>
import gql from "graphql-tag";
import moment from "moment";
import updateChartYLegendMixin from "@/mixins/updateChartYLegendMixin";
import DataInput from "@/components/ui/DataInput";
import VacancyStatChart from "./charts/StatChart";
import VacancyPolarAreaChart from "../../views/bi/campaigns/stats/VacancyPolarAreaChart";
import color from "../../assets/utils/color";

export default {
  components: {
    DataInput,
    VacancyStatChart,
    VacancyPolarAreaChart
  },
  mixins: [updateChartYLegendMixin],
  props: {
    maxY: { type: Number, default: null }
  },
  data() {
    return {
      internalMaxY: null,
      filter: {
        grouping: "REQUEST_OS",
        vacancyID: null,
        metrics: ["VISITORS", "APPLICATIONS"],
        fromTime: moment().subtract(7, "days"),
        toTime: null,
        interval: 1440,
        vacancyInfo: {
          vacancyJobType: null,
          occupation: null,
          occupationCategory: null,
          userGroup: null
        }
      },
      dataSource: "amountUnique",
      datacollection: null,
      pieChartData: null
    };
  },
  apollo: {
    biStats: {
      query: gql`
        query BiStats($filter: BIStatsFilter!) {
          biStats(filter: $filter) {
            interval
            numDatapoints
            datapoints {
              time
              interval
              amount
              amountUnique
              dropRate
              duration
              type
              info {
                source {
                  id
                  name
                }
                country
                city
                browser
                os
                device
                url
              }
            }
          }
        }
      `,
      variables() {
        return {
          filter: {
            grouping: this.filter.grouping,
            vacancyID: this.filter.vacancyID,
            interval: this.filter.interval,
            fromTime: this.filter.fromTime,
            toTime: this.filter.toTime,
            url: this.filter.url || undefined,
            metrics: ["VISITORS"],
            vacancyInfo: {
              vacancyJobType: this.filter.vacancyInfo.vacancyJobType,
              occupation: this.filter.vacancyInfo.occupation,
              occupationCategory: this.filter.vacancyInfo.occupationCategory,
              userGroup: this.filter.vacancyInfo.userGroup
            }
          }
        };
      }
    },
    deviceTypes: {
      query: gql`
        query BiStats($filter: BIStatsFilter!) {
          biStats(filter: $filter) {
            interval
            numDatapoints
            datapoints {
              time
              interval
              amount
              amountUnique
              dropRate
              type
              info {
                source {
                  id
                  name
                }
                country
                city
                browser
                os
                device
                url
              }
            }
          }
        }
      `,
      variables() {
        return {
          filter: {
            grouping: "REQUEST_OS",
            vacancyID: this.filter.vacancyID,
            interval: this.filter.interval,
            fromTime: this.filter.fromTime,
            toTime: this.filter.toTime,
            url: this.filter.url || undefined,
            metrics: ["VISITORS"],
            vacancyInfo: {
              vacancyJobType: this.filter.vacancyInfo.vacancyJobType,
              occupation: this.filter.vacancyInfo.occupation,
              occupationCategory: this.filter.vacancyInfo.occupationCategory,
              userGroup: this.filter.vacancyInfo.userGroup
            }
          }
        };
      },
      update: data => data.biStats
    }
  },
  computed: {
    chartMaxY() {
      return Math.max(this.internalMaxY, this.maxY);
    }
  },
  watch: {
    biStats(stats) {
      this.recreateChart(stats);
    },
    deviceTypes(stats) {
      this.recreatePieChart(stats);
    },
    dataSource() {
      this.recreateChart();
      this.recreatePieChart();
    }
  },
  methods: {
    recreateChart(stats = undefined) {
      if (stats === undefined) {
        stats = this.biStats;
      }

      if (!stats.datapoints || !this.filter.grouping) {
        this.datacollection = null;
        return;
      }

      stats.datapoints.sort((a, b) =>
        a.time > b.time ? 1 : a.time < b.time ? -1 : 0
      );

      let labels = [];
      for (let data of stats.datapoints) {
        let time = moment(data.time);
        if (labels.findIndex(val => val.diff(time) === 0) === -1) {
          labels.push(time);
        }
      }
      labels.sort(function(a, b) {
        return a.isBefore(b) ? -1 : 1;
      });

      let groupingInfoCol = {
        CITY: "city",
        COUNTRY: "country",
        REQUEST_OS: "os",
        REQUEST_DEVICE: "device",
        REQUEST_BROWSER: "browser",
        SOURCE: "source"
      }[this.filter.grouping];

      let groupings = {};
      for (let data of stats.datapoints) {
        let infoColContent = data.info[groupingInfoCol];
        if (infoColContent && infoColContent.name) {
          infoColContent = infoColContent.name;
        }

        if (groupings[infoColContent || "n.A."] === undefined) {
          groupings[infoColContent || "n.A."] = [];
        }
      }

      let maxValue = 1;
      for (let label of labels) {
        for (let data of stats.datapoints) {
          let time = moment(data.time);
          if (time.diff(label) === 0) {
            let infoColContent = data.info[groupingInfoCol];
            if (infoColContent && infoColContent.name) {
              infoColContent = infoColContent.name;
            }

            if (this.dataSource === "dropRate") {
              groupings[infoColContent || "n.A."].push(data.dropRate * 100);
            } else if (this.dataSource === "amount") {
              groupings[infoColContent || "n.A."].push(data.amount);
            } else {
              groupings[infoColContent || "n.A."].push(data.amountUnique);
            }
          }
        }
      }

      let datasets = [];
      for (let grouping of Object.keys(groupings)) {
        datasets.push({
          label: grouping,
          borderColor: color.colorFromString(grouping),
          data: groupings[grouping],
          fill: true,
          backgroundColor: color.colorFromString(grouping, 0.5),
          pointHoverBackgroundColor: "#000000",
          lineTension: 0
        });

        const currentMaxValue = Math.max.apply(Math, groupings[grouping]);
        if (currentMaxValue > maxValue) {
          maxValue = currentMaxValue;
        }
      }

      this.internalMaxY = maxValue;
      this.$emit("max-y-updated", maxValue);

      this.datacollection = {
        labels,
        datasets
      };
    },
    recreatePieChart(stats = undefined) {
      if (stats === undefined) {
        stats = this.biStats;
      }

      if (!stats.datapoints) {
        this.pieChartData = null;
        return;
      }

      let data = {
        Mobil: { amount: 0, amountUnique: 0, dropRate: 0 },
        Desktop: { amount: 0, amountUnique: 0, dropRate: 0 },
        "n.A.": { amount: 0, amountUnique: 0, dropRate: 0 }
      };
      for (let dp of stats.datapoints) {
        const deviceType = this.deviceTypeFromOS(dp.info.os);
        let dataInner = data[deviceType];
        dataInner.amount += dp.amount;
        dataInner.amountUnique += dp.amountUnique;
        dataInner.dropRate += dp.dropRate;
      }

      const labels = ["Mobil", "Desktop", "n.A."];

      let values = new Array(3);
      let backgroundColor = new Array(3);
      let borderColor = new Array(3);

      let datasets = [
        {
          label: "Endgeräte",
          fill: true
        }
      ];

      labels.forEach((label, index) => {
        backgroundColor[index] = color.colorFromString(String(label), 0.5);
        borderColor[index] = color.colorFromString(String(label), 0.5);
        values[index] = [data[label][this.dataSource || "n.A."]];
      });
      datasets[0].data = values;
      datasets[0].backgroundColor = backgroundColor;
      datasets[0].borderColor = borderColor;
      this.pieChartData = {
        labels,
        datasets
      };

      this.$nextTick().then(() => {
        this.$refs.chart.updateChart();
        this.$refs.piechart.updateChart();
      });
    },
    deviceTypeFromOS(os) {
      if (os == "Android" || os == "iOS") {
        return "Mobil";
      }
      if (os == "Linux" || os == "Windows" || os == "macOS") {
        return "Desktop";
      }
      return "n.A.";
    }
  }
};
</script>

<style lang="scss">
@import "@/assets/scss/components/bi/main";
</style>
